import React, { useCallback, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import {
  selectLoading,
  selectMainProduct,
  selectData as selectOrderDetails,
  selectOrderUpdating,
  selectProductUpdating,
} from '_store/order/selector';
import { getOrder, updateOrder, updateProduct } from '_store/order/slice';
import { selectCustomerId } from '_store/user/selector';

import Tabs from 'components/shared/Tabs';
import Card from 'components/shared/layouts/Card';
import Text from 'components/shared/layouts/Text';

import PhysicalInfoForm from './PhysicalInfoForm';
import ShippingAddressForm from './ShippingAddressForm';
import { mergeOrderBody, mergeProductBody, parsePhysicalInfo, parseShippingAddress } from './helpers';

const StyledContainer = styled.div`
  padding-top: ${theme.spaces.large}px;

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.regular}px;
    padding-bottom: ${theme.spaces.regular}px;
  }
`;

const StyledNavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledTabsContainer = styled.div`
  margin-bottom: ${theme.spaces.regular}px;
`;

const StyledLinkContainer = styled.div`
  cursor: pointer;

  *:hover {
    color: ${theme.colors.cyanBlue};
  }
`;

const StyledCard = styled(Card)`
  @media ${theme.devices.tablet} {
    margin-bottom: ${theme.spaces.regular}px;
  }
`;

const OrderDetailsView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const loading = useSelector(selectLoading);
  const orderUpdating = useSelector(selectOrderUpdating);
  const productUpdating = useSelector(selectProductUpdating);
  const orderDetails = useSelector(selectOrderDetails);
  const mainProduct = useSelector(selectMainProduct);
  const { orderId } = useParams();

  const handleBack = () => {
    navigate(-1);
  };

  const handleTabSelect = useCallback(
    (tabId) => {
      if (tabId === 'questionnaire') {
        navigate(`../order/${orderId}/questionnaire`);
      }
      if (tabId === 'chat') {
        navigate(`../order/${orderId}/chat`);
      }
    },
    [orderId]
  );

  const handleProductUpdate = useCallback(
    (values) => {
      if (customerId && orderId && mainProduct && values) {
        dispatch(
          updateProduct({ customerId, orderId, productId: mainProduct.id, body: mergeProductBody(mainProduct, values) })
        );
      }
    },
    [customerId, orderId, mainProduct]
  );

  const handleOrderUpdate = useCallback(
    (values) => {
      if (customerId && orderId && values) {
        dispatch(updateOrder({ customerId, orderId, body: mergeOrderBody({}, values) }));
      }
    },
    [customerId, orderId]
  );

  useEffect(() => {
    if (customerId && orderId) {
      dispatch(getOrder({ customerId, orderId }));
    }
  }, [customerId, orderId]);

  return (
    <StyledContainer>
      <StyledNavContainer>
        <StyledLinkContainer onClick={handleBack}>
          <Text color={theme.colors.gray.secondary}>&lt; Go back</Text>
        </StyledLinkContainer>
        <Text weight={700} color={theme.colors.deepBlueGreen}>
          Order: #{orderDetails?.crmOrderId}
        </Text>
      </StyledNavContainer>
      <StyledTabsContainer>
        <Tabs
          color={theme.colors.deepBlueGreen}
          selected="order-details"
          items={[
            {
              tabId: 'order-details',
              iconAlias: ICON_ALIASES.document,
              title: 'Order Details',
            },
            {
              tabId: 'chat',
              iconAlias: ICON_ALIASES.message,
              title: 'Chat',
            },
            {
              tabId: 'questionnaire',
              iconAlias: ICON_ALIASES.documentNormal,
              title: 'Questionnaire',
            },
          ]}
          onSelect={handleTabSelect}
        />
      </StyledTabsContainer>
      <Row>
        <Col xs={12} md={6}>
          <StyledCard>
            <Text size={theme.fontSizes.large2X} weight={700} bottomSpace={theme.spaces.large} desktop>
              Physical Information
            </Text>
            <Text size={theme.fontSizes.large} weight={700} bottomSpace={theme.spaces.large} centered mobile>
              Physical Information
            </Text>
            <PhysicalInfoForm
              loading={productUpdating}
              error={null}
              submitDisabled={loading || productUpdating}
              defaultValues={parsePhysicalInfo(mainProduct)}
              onSubmit={handleProductUpdate}
            />
          </StyledCard>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Text size={theme.fontSizes.large2X} weight={700} bottomSpace={theme.spaces.large} desktop>
              Shipping Address
            </Text>
            <Text size={theme.fontSizes.large} weight={700} bottomSpace={theme.spaces.large} centered mobile>
              Shipping Address
            </Text>
            <ShippingAddressForm
              loading={orderUpdating}
              error={null}
              submitDisabled={loading || orderUpdating}
              defaultValues={parseShippingAddress(orderDetails)}
              onSubmit={handleOrderUpdate}
            />
          </Card>
        </Col>
      </Row>
    </StyledContainer>
  );
};

export default OrderDetailsView;
